// components/PropertyTimeline.js
//
// Horizontale tijdlijn met alle (relevante) woningen onder mekaar, een beetje
// zoals Guesty's algemene kalender: elke rij is een woning, de x-as zijn
// dagen/uren, en boekingen/schoonmaak worden als balkjes op hun échte
// positie getekend (geen dag-gebaseerde afronding zoals de vorige
// maandkalender). Daardoor tonen turnover-dagen (check-out 11:00, check-in
// 15:00 dezelfde dag) hier vanzelf een écht tijdsgat i.p.v. twee balken die
// toevallig dezelfde dagcel delen - dit was niet mogelijk in FullCalendar's
// gratis maandweergave, vandaar deze eigen (licentie-vrije) opbouw.
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import PlatformBadge from './PlatformBadge';
import CleaningInfoModal from './CleaningInfoModal';
import { getAuthToken, getAuthUser } from '../utils/authSession';
import { cacheGet, cacheSet } from '../utils/dataCache';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import './PropertyTimeline.css';
import { sortedNames } from '../utils/sortNames';

// Zichtbaar venster + schaal, opgezet als een compacte "algemene kalender"
// (vergelijkbaar met Guesty's multi-kalender): smalle dagkolommen zodat in
// één oogopslag meerdere maanden zichtbaar zijn, met een dunne rode lijn die
// het huidige moment markeert.
// Venster bewust zo breed dat je zonder ooit op "Vorige"/"Volgende" te
// drukken altijd meteen ~1 maand terug en ~2 maand vooruit kan bekijken
// (gewoon door binnen dit ene venster horizontaal te scrollen) - PAST_DAYS
// is de 1 maand terug, WINDOW_DAYS - PAST_DAYS is de ~2 maand vooruit vanaf
// vandaag.
const PAST_DAYS = 30;
const WINDOW_DAYS = 90;
const DESKTOP_PX_PER_DAY = 64;
// Op een telefoon was elke dagkolom even breed als op desktop, waardoor er
// nog maar 3 à 4 dagen in beeld pasten naast de woningkolom - dus smallere
// kolommen op mobiel zodat je in één oogopslag meer dagen ziet.
const MOBILE_PX_PER_DAY = 34;
const MIN_BAR_WIDTH_PX = 4;

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const defaultViewStart = () => addDays(startOfDay(new Date()), -PAST_DAYS);

const formatDateTime = (date, locale) => new Date(date).toLocaleString(locale, {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
});

const formatTimeOnly = (date, locale) => new Date(date).toLocaleString(locale, {
  hour: '2-digit', minute: '2-digit'
});

// Standaard schoonmaakduur in milliseconden voor het vooraf invullen van het
// inplan-formulier: gebruikt de "aantal uur voor schoonmaker" die de admin
// per woning instelde (cleaningHours), en valt enkel terug op een generieke
// duur wanneer dat veld niet is ingesteld. Voorheen stond dit hard op +2u/+3u
// vast, ongeacht wat er bij de woning zelf geconfigureerd stond - dat gaf een
// "Duur" bij Ingepland die niet overeenkwam met de effectief ingestelde uren.
const defaultCleaningDurationMs = (cleaningHours, fallbackHours) => {
  const hours = Number(cleaningHours);
  const validHours = !isNaN(hours) && hours > 0 ? hours : fallbackHours;
  return validHours * 60 * 60 * 1000;
};

function PropertyTimeline({ properties, userRole, onEditCleaning }) {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith('en');
  const isFrench = i18n.language?.startsWith('fr');
  const locale = isFrench ? 'fr-BE' : isEnglish ? 'en-GB' : 'nl-NL';
  const dayLocale = isFrench ? 'fr-BE' : isEnglish ? 'en-GB' : 'nl-NL';

  const [viewStart, setViewStart] = useState(defaultViewStart);
  // Kolombreedte per dag: smaller op mobiel (zie MOBILE_PX_PER_DAY hierboven).
  const [dayWidth, setDayWidth] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth <= 768 ? MOBILE_PX_PER_DAY : DESKTOP_PX_PER_DAY
  ));

  useEffect(() => {
    const handleResize = () => {
      setDayWidth(window.innerWidth <= 768 ? MOBILE_PX_PER_DAY : DESKTOP_PX_PER_DAY);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Horizontaal scrollende container - gebruikt om bij het (opnieuw) laden
  // automatisch zo te scrollen dat "vandaag" links in beeld staat (met de
  // mogelijkheid om verder naar links te scrollen voor recent verleden),
  // i.p.v. helemaal links te beginnen bij het begin van het venster.
  const scrollRef = useRef(null);
  const [eventsByProperty, setEventsByProperty] = useState({});
  const [loading, setLoading] = useState(true);
  const [bookingModalData, setBookingModalData] = useState(null);
  // Eigen in-app melding/bevestiging i.p.v. window.alert()/window.confirm().
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [confirmState, setConfirmState] = useState(null);
  // Admin: infovenster bij een klik op een schoonmaakmoment (met knop "Bewerken").
  const [adminCleaningInfo, setAdminCleaningInfo] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };
  const askConfirm = (message, onConfirm) => setConfirmState({ message, onConfirm });
  // Louter informatief modaltje (enkel "Sluiten") - gebruikt wanneer een
  // verhuurder wel de details van een schoonmaakmoment mag zien, maar het
  // (nog) niet mag verwijderen; zie handleCleaningClick hieronder.
  const showInfo = (message) => setConfirmState({ message, onConfirm: null });
  // Eigen user-id van de ingelogde verhuurder, om te bepalen of hij/zij zelf
  // de "maker" (createdBy) van een schoonmaakmoment is - enkel dan mag hij
  // die eventueel nog zelf verwijderen (zie canDeleteCleaning hieronder).
  const currentUserId = JSON.parse(getAuthUser() || 'null')?.id;
  // Huidig moment, elke minuut ververst - bepaalt de positie van de rode
  // "nu"-lijn doorheen alle rijen (zoals in Guesty's algemene kalender).
  const [now, setNow] = useState(() => new Date());

  const [showCleaningModal, setShowCleaningModal] = useState(false);
  const [cleaningModalPropertyId, setCleaningModalPropertyId] = useState('');
  const [unscheduledForModal, setUnscheduledForModal] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [formData, setFormData] = useState({
    startDate: new Date(),
    endDate: new Date(new Date().setHours(new Date().getHours() + 2)),
    notes: '',
    // Vrije tekst (bv. een toegangscode) - los van "notes", enkel zichtbaar
    // voor de schoonmaker die uiteindelijk effectief aangenomen wordt voor
    // deze taak (zie backend accessInfo-gating in /api/my-tasks).
    access: '',
    // Aantal schoonmakers dat voor DEZE taak verwacht wordt - enkel door de
    // admin aan te passen. Leeg = valt terug op de standaard van de woning.
    cleanerCount: ''
  });

  const viewEnd = useMemo(() => addDays(viewStart, WINDOW_DAYS), [viewStart]);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < WINDOW_DAYS; i++) arr.push(addDays(viewStart, i));
    return arr;
  }, [viewStart]);

  // Groepeert de zichtbare dagen per maand (voor de maand-header-rij erboven),
  // elke groep kent zijn breedte in px (aantal dagen x kolombreedte).
  const monthGroups = useMemo(() => {
    const groups = [];
    days.forEach((day) => {
      const label = day.toLocaleDateString(dayLocale, { month: 'long', year: 'numeric' });
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.count += 1;
      } else {
        groups.push({ label, shortLabel: day.toLocaleDateString(dayLocale, { month: 'short' }), count: 1 });
      }
    });
    return groups;
  }, [days, dayLocale]);

  const todayKey = new Date().toDateString();

  // Elke minuut de klok verversen, zodat de rode "nu"-lijn bijblijft als de
  // pagina lang openstaat (geen zware polling, gewoon een lokale timer).
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // silent = op de achtergrond verversen zonder laad-scherm (zo springt de
  // tijdlijn niet terug naar "vandaag" na een kleine wijziging).
  const timelineCacheKey = `timeline|${viewStart.getTime()}|${(properties || []).map((p) => p.id).sort().join(',')}`;
  const fetchOverview = useCallback(async (silent = false) => {
    if (!properties || properties.length === 0) {
      setEventsByProperty({});
      setLoading(false);
      return;
    }
    if (silent !== true && !cacheGet(timelineCacheKey)) setLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/calendar-overview`, {
        params: { start: viewStart.toISOString(), end: viewEnd.toISOString() },
        headers: { Authorization: `Bearer ${token}` }
      });
      const grouped = {};
      (response.data.events || []).forEach((item) => {
        // Nog niet ingepland (wacht op goedkeuring van de admin): nog niet in de kalender.
        if (item.type === 'cleaning' && item.status === 'PENDING_APPROVAL') return;
        if (!grouped[item.propertyId]) grouped[item.propertyId] = [];
        grouped[item.propertyId].push(item);
      });
      setEventsByProperty(grouped);
      cacheSet(timelineCacheKey, grouped);
    } catch (error) {
      console.error('Fout bij ophalen kalender-overzicht:', error);
      setEventsByProperty({});
    } finally {
      setLoading(false);
    }
  }, [properties, viewStart, viewEnd, timelineCacheKey]);

  // Eerder opgehaalde kalender meteen tonen (geen "Laden..." bij elke tabwissel).
  useLayoutEffect(() => {
    const hit = cacheGet(timelineCacheKey);
    if (hit) { setEventsByProperty(hit); setLoading(false); }
  }, [timelineCacheKey]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Scrolt automatisch zodat "vandaag" links in beeld staat zodra dit
  // venster vandaag bevat (het standaardvenster en na "Vandaag"), of
  // anders gewoon naar het begin van het (verleden/toekomstige) venster na
  // "Vorige"/"Volgende" - de gebruiker kan daarna vrij verder naar links
  // scrollen om recent verleden te bekijken.
  useEffect(() => {
    if (loading || !scrollRef.current) return;
    const dayMs = 24 * 60 * 60 * 1000;
    const todayOffsetDays = Math.round((startOfDay(new Date()) - viewStart) / dayMs);
    scrollRef.current.scrollLeft = (todayOffsetDays >= 0 && todayOffsetDays < WINDOW_DAYS)
      ? todayOffsetDays * dayWidth
      : 0;
  }, [loading, viewStart, dayWidth]);

  const getOffsetPx = useCallback((date) => {
    const hours = (new Date(date) - viewStart) / (60 * 60 * 1000);
    return hours * (dayWidth / 24);
  }, [viewStart, dayWidth]);

  const computeBarStyle = useCallback((evStart, evEnd) => {
    const s = new Date(evStart);
    const e = new Date(evEnd);
    const clampedStart = s < viewStart ? viewStart : s;
    const clampedEnd = e > viewEnd ? viewEnd : e;
    const left = getOffsetPx(clampedStart);
    const width = Math.max(getOffsetPx(clampedEnd) - left, MIN_BAR_WIDTH_PX);
    return { left, width };
  }, [viewStart, viewEnd, getOffsetPx]);

  // Positie (in px, t.o.v. het begin van de dagen-kolommen) van de rode
  // "nu"-lijn - enkel tonen als het huidige moment binnen het zichtbare
  // venster valt.
  const nowLinePx = (now >= viewStart && now <= viewEnd) ? getOffsetPx(now) : null;

  const goPrev = () => setViewStart((prev) => addDays(prev, -WINDOW_DAYS));
  const goNext = () => setViewStart((prev) => addDays(prev, WINDOW_DAYS));
  const goToday = () => setViewStart(defaultViewStart());

  const fetchUnscheduledForProperty = async (propertyId) => {
    if (!propertyId) {
      setUnscheduledForModal([]);
      return;
    }
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/unscheduled-bookings`, {
        params: { propertyId },
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnscheduledForModal(response.data);
    } catch (error) {
      console.error('Fout bij laden niet-ingeplande boekingen:', error);
      setUnscheduledForModal([]);
    }
  };

  const openCleaningModal = (propertyId, bookingId = '') => {
    setCleaningModalPropertyId(propertyId || '');
    setSelectedBookingId(bookingId || '');
    fetchUnscheduledForProperty(propertyId);

    const events = propertyId ? (eventsByProperty[propertyId] || []) : [];
    const booking = bookingId ? events.find((e) => e.type === 'booking' && e.id === bookingId) : null;
    const cleaningHours = properties.find((p) => p.id === propertyId)?.cleaningHours;
    const seededCleanerCount = properties.find((p) => p.id === propertyId)?.defaultCleanerCount ?? '';

    if (booking) {
      const start = new Date(booking.checkOut);
      start.setHours(start.getHours() + 1);
      const end = new Date(start.getTime() + defaultCleaningDurationMs(cleaningHours, 3));
      setFormData({ startDate: start, endDate: end, notes: '', access: '', cleanerCount: seededCleanerCount });
    } else {
      const start = new Date();
      setFormData({
        startDate: start,
        endDate: new Date(start.getTime() + defaultCleaningDurationMs(cleaningHours, 2)),
        notes: '',
        access: '',
        cleanerCount: seededCleanerCount
      });
    }
    setShowCleaningModal(true);
  };

  const handleSelectPropertyInModal = (propertyId) => {
    setCleaningModalPropertyId(propertyId);
    setSelectedBookingId('');
    fetchUnscheduledForProperty(propertyId);
    setFormData((prev) => ({ ...prev, cleanerCount: properties.find((p) => p.id === propertyId)?.defaultCleanerCount ?? '' }));
  };

  const handleSelectBookingInModal = (bookingId) => {
    setSelectedBookingId(bookingId);
    const booking = unscheduledForModal.find((b) => b.id === bookingId);
    if (booking) {
      const cleaningHours = properties.find((p) => p.id === cleaningModalPropertyId)?.cleaningHours;
      const start = new Date(booking.checkOut);
      start.setHours(start.getHours() + 1);
      const end = new Date(start.getTime() + defaultCleaningDurationMs(cleaningHours, 3));
      setFormData((prev) => ({ ...prev, startDate: start, endDate: end }));
    }
  };

  const resetCleaningModal = () => {
    setShowCleaningModal(false);
    setCleaningModalPropertyId('');
    setSelectedBookingId('');
    setUnscheduledForModal([]);
  };

  const handleSaveCleaning = async (e) => {
    e.preventDefault();
    if (!validateRequiredFields(e.target)) {
      showToast(t('common.fillRequired'), 'error');
      return;
    }
    if (!cleaningModalPropertyId) {
      showToast(t('calendar.selectPropertyRequired'), 'error');
      return;
    }
    if (formData.startDate >= formData.endDate) {
      showToast(t('calendar.endAfterStart'), 'error');
      return;
    }
    try {
      const token = getAuthToken();
      const response = await axios.post(`${API_URL}/api/cleaning-tasks`, {
        propertyId: cleaningModalPropertyId,
        bookingId: selectedBookingId || undefined,
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString(),
        notes: formData.notes,
        ...(userRole === 'ADMIN' ? { expectedCleanerCount: formData.cleanerCount || undefined } : {})
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Een verhuurder-toegevoegde taak komt niet meteen open te staan, maar
      // wacht eerst op goedkeuring door een admin - andere melding dan bij
      // een meteen-bevestigde (admin-)taak, zodat dit duidelijk is.
      showToast(
        response.data.task?.status === 'PENDING_APPROVAL' ? t('calendar.cleaningPendingApproval') : t('calendar.cleaningScheduled'),
        'success'
      );
      resetCleaningModal();
      // Stil verversen: geen laad-scherm, de kalender blijft staan waar hij staat.
      fetchOverview(true);
    } catch (error) {
      console.error('Fout bij opslaan:', error);
      showToast(error.response?.data?.error || t('calendar.saveError'), 'error');
    }
  };

  // Bevestiging gebeurt al bij de aanroeper (handleCleaningClick hieronder)
  // via askConfirm() - geen aparte window.confirm()-guard hier meer nodig.
  const handleDeleteCleaning = async (taskId) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/api/cleaning-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchOverview(true);
    } catch (error) {
      console.error('Fout bij verwijderen:', error);
      showToast(t('calendar.deleteFailed'), 'error');
    }
  };

  const statusLabels = {
    OPEN: t('calendar.statusOpen'),
    PENDING_CONFIRMATION: t('calendar.statusPending'),
    ASSIGNED: t('calendar.statusAssigned'),
    COMPLETED: t('calendar.statusCompleted'),
    CANCELLED: t('calendar.statusCancelled')
  };

  const handleBookingClick = (property, booking, hasNoCleaning) => {
    setBookingModalData({
      propertyId: property.id,
      propertyName: property.name,
      cleaningTier: property.cleaningTier,
      dbId: booking.id,
      platform: booking.platform,
      displayPlatform: booking.bookingSource || booking.platform,
      guestName: booking.guestName,
      guestEmail: booking.guestEmail,
      checkIn: booking.checkIn,
      checkOut: booking.checkOut,
      hasNoCleaning
    });
  };

  // Enkel de admin ziet de interne status (Open/Ingepland/Toegewezen/...) -
  // voor een verhuurder is dat interne keukeninformatie die niets toevoegt
  // en enkel verwarrend is (zie ook het verwijderrecht hieronder, dat net op
  // diezelfde status steunt).
  //
  // Een verhuurder mag een schoonmaakmoment enkel zelf verwijderen als hij
  // het zelf heeft aangemaakt, het nog niet aanvaard/toegewezen is (nog geen
  // schoonmaker gekoppeld, nog in PENDING_APPROVAL) en het nog niet in het
  // verleden ligt - exact dezelfde regel als hierboven in backend/index.js
  // (die blijft de échte grens; dit hier is enkel voor een consistente UI).
  const canDeleteCleaning = (cleaning) => {
    if (userRole === 'ADMIN') return true;
    if (userRole !== 'VERHUURDER') return false;
    const hasCleaner = Boolean(cleaning.cleanerId || cleaning.cleanerId2 || cleaning.cleanerId3);
    const isOwnCreation = cleaning.createdBy === currentUserId;
    const isStillPending = cleaning.status === 'PENDING_APPROVAL';
    const isInFuture = new Date(cleaning.startDate) > new Date();
    return isOwnCreation && isStillPending && !hasCleaner && isInFuture;
  };

  const handleCleaningClick = (cleaning) => {
    // Admin-weergave die een "onEditCleaning"-callback meekreeg (bv. de
    // bedrijfsbrede multikalender in Admin.js) opent meteen de volledige
    // "bewerk schoonmaak"-modal i.p.v. het alleen-lezen infovenstertje
    // hieronder - zo kan de admin hier ook echt iets aanpassen, niet enkel
    // bekijken/verwijderen. Andere gebruikers (verhuurder in zijn eigen
    // dashboard) krijgen dit niet mee en behouden het bestaande gedrag.
    if (userRole === 'ADMIN' && onEditCleaning) {
      setAdminCleaningInfo(cleaning);
      return;
    }
    let info = t('calendar.cleaningInfo', {
      start: formatTimeOnly(cleaning.startDate, locale),
      end: formatTimeOnly(cleaning.endDate, locale)
    });
    if (userRole === 'ADMIN') {
      info += `\n${t('calendar.statusLine', { status: statusLabels[cleaning.status] || cleaning.status })}`;
    }
    const cleanerNames = sortedNames([cleaning.cleaner_name, cleaning.cleaner2_name, cleaning.cleaner3_name]).join(', ');
    if (cleanerNames) {
      info += `\n${t('calendar.cleaner')}: ${cleanerNames}`;
    }
    if (canDeleteCleaning(cleaning)) {
      askConfirm(`${info}\n\n${t('calendar.deleteCleaningPrompt')}`, () => handleDeleteCleaning(cleaning.id));
    } else {
      showInfo(info);
    }
  };

  if (!properties || properties.length === 0) {
    return <div className="timeline-empty">{t('calendar.noPropertiesForTimeline')}</div>;
  }

  return (
    <div className="property-timeline">
      <div className="timeline-toolbar">
        <div className="timeline-nav-row">
          <button type="button" className="timeline-nav-btn" onClick={goPrev} aria-label={t('calendar.previous')}>&#8249;</button>
          <button type="button" className="timeline-nav-btn" onClick={goNext} aria-label={t('calendar.next')}>&#8250;</button>
          <button type="button" className="timeline-nav-today" onClick={goToday}>{t('calendar.today')}</button>
        </div>
        <button
          onClick={() => openCleaningModal(properties.length === 1 ? properties[0].id : '')}
          className="timeline-add-cleaning-btn"
        >
          + {t('calendar.addCleaning')}
        </button>
      </div>

      {loading ? (
        <div className="calendar-loading" aria-busy="true" />
      ) : (
        <div className="timeline-body">
          {/* Namen/foto's staan bewust helemaal BUITEN de horizontaal
              scrollende container (i.p.v. een "position: sticky"-kolom
              erbinnen) - zo kunnen ze structureel nooit mee scrollen of
              verdwijnen, ongeacht hoe ver je naar rechts (de toekomst in)
              scrolt. Elke rij hier moet exact dezelfde hoogte hebben als de
              overeenkomstige rij in ".timeline-scroll" hiernaast, zodat beide
              kolommen mooi uitlijnen - zie de vaste hoogtes in
              PropertyTimeline.css. */}
          <div className="timeline-labels-col">
            <div className="timeline-label-col timeline-label-col-header" />
            {properties.map((property) => {
              const cleaningBlocked = userRole === 'VERHUURDER' && property.cleaningTier === 'NONE';
              return (
                <div className="timeline-label-col" key={property.id}>
                  {property.photoUrl ? (
                    <img className="timeline-property-photo" src={`${API_URL}${property.photoUrl}`} alt="" />
                  ) : (
                    <span className="timeline-property-photo timeline-property-photo-placeholder">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 11L12 3L21 11M5 9.222V20H9.5V14H14.5V20H19V9.222" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </span>
                  )}
                  <span className="timeline-property-text">
                    <span className="timeline-property-name">{property.name}</span>
                  </span>
                </div>
              );
            })}
          </div>

          <div className="timeline-scroll" ref={scrollRef}>
            <div className="timeline-header-columns" style={{ width: WINDOW_DAYS * dayWidth }}>
              <div className="timeline-months-header">
                {monthGroups.map((group, idx) => (
                  <div key={`${group.label}-${idx}`} className="timeline-month-label" style={{ width: group.count * dayWidth }}>
                    {/* Past de volle naam niet (bv. nog maar 1-2 dagen van die maand in beeld),
                        dan een korte naam of niets - nooit afbreken over 2 regels. */}
                    {group.count * dayWidth >= 125 ? group.label : (group.count * dayWidth >= 46 ? group.shortLabel : '')}
                  </div>
                ))}
              </div>
              <div className="timeline-days-header">
                {days.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={`timeline-day-label ${day.toDateString() === todayKey ? 'is-today' : ''}`}
                    style={{ width: dayWidth }}
                  >
                    <span className="timeline-day-weekday">{day.toLocaleDateString(dayLocale, { weekday: 'short' })}</span>
                    <span className="timeline-day-number">{day.getDate()}</span>
                  </div>
                ))}
              </div>
            </div>

            {properties.map((property) => {
              const events = eventsByProperty[property.id] || [];
              // "hasNoCleaning" hieronder moet over ALLE schoonmaaktaken van
              // deze woning kijken (ook net buiten dit venster), maar de
              // balkjes zelf mogen enkel getekend worden voor gebeurtenissen
              // die dit venster ook effectief overlappen - anders klemt
              // computeBarStyle hieronder een volledig-buiten-beeld
              // gebeurtenis (bv. een allang voorbije schoonmaak, van vóór
              // "Volgende" ingedrukt werd) alsnog vast op links (left=0) met
              // de minimum-breedte, wat als een storend "spook-balkje" op
              // dag 1 oogde.
              const allCleanings = events.filter((e) => e.type === 'cleaning');
              const bookings = events
                .filter((e) => e.type === 'booking')
                .filter((e) => new Date(e.checkOut) > viewStart && new Date(e.checkIn) < viewEnd);
              const cleanings = allCleanings
                .filter((e) => new Date(e.endDate) > viewStart && new Date(e.startDate) < viewEnd);

              return (
                <div className="timeline-track" style={{ width: WINDOW_DAYS * dayWidth }} key={property.id}>
                  {days.map((day, idx) => (
                    <div
                      key={day.toISOString()}
                      className={`timeline-gridline ${day.toDateString() === todayKey ? 'is-today' : ''}`}
                      style={{ left: idx * dayWidth, width: dayWidth }}
                    />
                  ))}

                  {/* Rode "nu"-lijn: enkel hier binnen .timeline-track (per
                      woning-rij) getekend, NIET meer ook in de dagen-header
                      erboven - anders liep de lijn zichtbaar door de
                      dag-nummers/weekdag-rij heen, wat niet de bedoeling is. */}
                  {nowLinePx !== null && (
                    <div className="timeline-now-line" style={{ left: nowLinePx }} />
                  )}

                  {bookings.map((booking) => {
                    const { left, width } = computeBarStyle(booking.checkIn, booking.checkOut);
                    const hasNoCleaning = !allCleanings.some((c) => c.bookingId === booking.id);
                    const displayPlatform = booking.bookingSource || booking.platform;
                    return (
                      <div
                        key={`booking-${booking.id}`}
                        className={`timeline-bar timeline-bar-booking ${userRole === 'ADMIN' && hasNoCleaning ? (booking.cleaningRejected ? 'timeline-bar-rejected' : (booking.needsCleaning ? 'timeline-bar-no-cleaning' : '')) : ''}`}
                        style={{ left, width }}
                        title={`${booking.guestName || t('calendar.guest')} - ${displayPlatform}\n${formatDateTime(booking.checkIn, locale)} → ${formatDateTime(booking.checkOut, locale)}`}
                        onClick={() => handleBookingClick(property, booking, hasNoCleaning)}
                      >
                        <span className="timeline-bar-label">
                          <PlatformBadge platform={displayPlatform} className="platform-badge-only" />
                        </span>
                      </div>
                    );
                  })}

                  {cleanings.map((cleaning) => {
                    const { left, width } = computeBarStyle(cleaning.startDate, cleaning.endDate);
                    const isPast = new Date(cleaning.endDate) < now;
                    const isCompleted = isPast || ['COMPLETED', 'INVOICED'].includes(cleaning.status);
                    // 3 kleuren i.p.v. 2, voor zowel admin als verhuurder:
                    // rood zolang er nog geen schoonmaker toegewezen is
                    // (Ingepland), oranje zodra die er wel is maar de
                    // schoonmaak nog niet voltooid is (Toegewezen), groen
                    // zodra voltooid/uitbetaald of al verstreken (Voltooid).
                    const cleaningStatusClass = isCompleted
                      ? 'timeline-bar-cleaning-completed'
                      // Verhuurder: enkel blauw (komt nog) of groen (voltooid).
                      : userRole !== 'ADMIN' ? 'timeline-bar-cleaning-assigned'
                      // Admin: rood = niemand toegewezen, oranje = minimum aantal schoonmakers niet bereikt.
                      : ((cleaning.status === 'ASSIGNED' || Number(cleaning.assigned_cleaner_count) > 0) ? (Number(cleaning.assigned_cleaner_count) < Number(cleaning.expected_cleaner_count) ? 'timeline-bar-cleaning-partial' : 'timeline-bar-cleaning-assigned') : 'timeline-bar-cleaning-unassigned');
                    return (
                      <div
                        key={`cleaning-${cleaning.id}`}
                        className={`timeline-bar timeline-bar-cleaning ${cleaningStatusClass}`}
                        style={{ left, width }}
                        title={`${cleaning.title}\n${formatDateTime(cleaning.startDate, locale)} → ${formatDateTime(cleaning.endDate, locale)}`}
                        onClick={() => handleCleaningClick(cleaning)}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal voor schoonmaak inplannen - toont een woning-keuze zodra er
          meer dan 1 woning op deze tijdlijn staat. */}
      {showCleaningModal && (
        <div className="modal-overlay" onClick={resetCleaningModal}>
          <div className="modal cleaning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('calendar.scheduleCleaning')}</h2>
              <button className="modal-close" onClick={resetCleaningModal}>✖</button>
            </div>
            <form onSubmit={handleSaveCleaning} noValidate onInput={clearFieldInvalid}>
              {properties.length > 1 && (
                <div className="form-group">
                  <label>{t('calendar.selectProperty')} *</label>
                  <select
                    value={cleaningModalPropertyId}
                    onChange={(e) => handleSelectPropertyInModal(e.target.value)}
                    required
                  >
                    <option value="">{t('calendar.selectPropertyPlaceholder')}</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>{t('calendar.bookingOptional')}</label>
                <select
                  value={selectedBookingId}
                  onChange={(e) => handleSelectBookingInModal(e.target.value)}
                  disabled={!cleaningModalPropertyId}
                >
                  <option value="">{t('calendar.noBookingOption')}</option>
                  {unscheduledForModal.map((booking) => (
                    <option key={booking.id} value={booking.id}>
                      {booking.guestName || t('calendar.guest')} ({new Date(booking.checkIn).toLocaleDateString('nl-NL')} - {new Date(booking.checkOut).toLocaleDateString('nl-NL')})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('calendar.start')} *</label>
                  <input
                    type="datetime-local"
                    value={toLocalInputValue(formData.startDate)}
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      setFormData((prev) => {
                        const duration = prev.endDate.getTime() - prev.startDate.getTime();
                        return { ...prev, startDate: date, endDate: new Date(date.getTime() + duration) };
                      });
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('calendar.end')} *</label>
                  <input
                    type="datetime-local"
                    value={toLocalInputValue(formData.endDate)}
                    onChange={(e) => setFormData((prev) => ({ ...prev, endDate: new Date(e.target.value) }))}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('calendar.notes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={t('calendar.notesPlaceholder')}
                  rows="3"
                />
              </div>


              {userRole === 'ADMIN' && (
                <div className="form-group">
                  <label>{t('admin.expectedCleanerCountLabel')}</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.cleanerCount}
                    onChange={(e) => setFormData({ ...formData, cleanerCount: e.target.value })}
                  />
                  <p className="form-hint">{t('admin.expectedCleanerCountHint')}</p>
                </div>
              )}

              <div className="modal-buttons">
                <button type="button" onClick={resetCleaningModal} className="cancel-btn">
                  {t('common.cancel')}
                </button>
                <button type="submit" className="submit-btn">
                  {t('calendar.schedule')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal met boekingdetails (alleen-lezen). */}
      {bookingModalData && (
        <div className="modal-overlay" onClick={() => setBookingModalData(null)}>
          <div className="modal booking-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><PlatformBadge platform={bookingModalData.displayPlatform} />{bookingModalData.guestName || t('calendar.guest')} - {bookingModalData.displayPlatform}</h2>
              <button className="modal-close" onClick={() => setBookingModalData(null)}>✖</button>
            </div>

            <div className="booking-info-body">
              <p><strong>{t('common.address')}:</strong> {bookingModalData.propertyName}</p>
              <p><strong>{t('calendar.start')}:</strong> {formatDateTime(bookingModalData.checkIn, locale)}</p>
              <p><strong>{t('calendar.end')}:</strong> {formatDateTime(bookingModalData.checkOut, locale)}</p>
              <p><strong>{t('calendar.guestName')}:</strong> {bookingModalData.guestName || t('calendar.guestInfoMissing')}</p>
              <p><strong>{t('calendar.guestEmail')}:</strong> {bookingModalData.guestEmail || t('calendar.guestInfoMissing')}</p>
              {/* Klant zonder schoonmaak: nergens iets over schoonmaak tonen. */}
              {!(userRole === 'VERHUURDER' && bookingModalData.cleaningTier === 'NONE') && (
                <p><strong>{t('calendar.cleaningLabel')}:</strong> {bookingModalData.hasNoCleaning ? t('calendar.cleaningNotPlanned') : t('calendar.cleaningPlanned')}</p>
              )}
            </div>

            {bookingModalData.hasNoCleaning && !(userRole === 'VERHUURDER' && bookingModalData.cleaningTier === 'NONE') && (
              <div className="booking-info-cleaning-section">
                <button
                  type="button"
                  className="submit-btn"
                  onClick={() => {
                    const { propertyId, dbId } = bookingModalData;
                    setBookingModalData(null);
                    openCleaningModal(propertyId, dbId);
                  }}
                >
                  {t('calendar.scheduleCleaningNow')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {toast.show && (
        <div className={`toast-message ${toast.type}`}>{toast.message}</div>
      )}

      {/* Admin: infovenster van een schoonmaakmoment (tijden, status,
          schoonmakers) met een knop om het volledige bewerkformulier te openen. */}
      {adminCleaningInfo && (
        <CleaningInfoModal
          locale={locale}
          info={{
            id: adminCleaningInfo.id,
            propertyName: properties.find((p) => p.id === adminCleaningInfo.propertyId)?.name,
            start: adminCleaningInfo.startDate,
            end: adminCleaningInfo.endDate,
            status: adminCleaningInfo.status,
            cleaners: [
              { id: adminCleaningInfo.cleanerId, name: adminCleaningInfo.cleaner_name },
              { id: adminCleaningInfo.cleanerId2, name: adminCleaningInfo.cleaner2_name },
              { id: adminCleaningInfo.cleanerId3, name: adminCleaningInfo.cleaner3_name },
            ].filter((c) => c.id),
            expectedCount: adminCleaningInfo.expected_cleaner_count,
          }}
          onChanged={() => fetchOverview(true)}
          onClose={() => setAdminCleaningInfo(null)}
          onDelete={(id) => {
            setAdminCleaningInfo(null);
            askConfirm(t('calendar.deleteCleaningPrompt'), () => handleDeleteCleaning(id));
          }}
          onEdit={(id) => { setAdminCleaningInfo(null); onEditCleaning(id); }}
        />
      )}

      {/* Eigen bevestigingsmodaltje i.p.v. window.confirm() - askConfirm()
          hierboven vult confirmState.message/onConfirm; "Verwijderen" voert
          onConfirm uit en sluit het modaltje. */}
      {confirmState && (
        <div className="modal-overlay" onClick={() => setConfirmState(null)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{confirmState.onConfirm ? t('common.confirm') : t('calendar.cleaningInfoTitle')}</h2>
              <button className="modal-close" onClick={() => setConfirmState(null)}>✖</button>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: 'pre-line' }}>{confirmState.message}</p>
            </div>
            <div className="modal-buttons">
              {confirmState.onConfirm ? (
                <>
                  <button type="button" className="cancel-btn" onClick={() => setConfirmState(null)}>
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={() => { const { onConfirm } = confirmState; setConfirmState(null); onConfirm(); }}
                  >
                    {t('common.delete')}
                  </button>
                </>
              ) : (
                <button type="button" className="submit-btn" onClick={() => setConfirmState(null)}>
                  {t('common.close')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Formatteert een Date naar de waarde die <input type="datetime-local">
// verwacht (lokale tijd, geen tijdzone-conversie zoals toISOString() die zou
// doen - dat zou het weergegeven uur fout doen aflezen).
const toLocalInputValue = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default PropertyTimeline;
